import { DataService } from "@/services/data";
import { RouteObject } from "react-router-dom";

function caseFilters(query: string): Record<string, string> {
    if (!query) return {};
    if (/^[a-zA-Z0-9]{15,18}$/.test(query)) return { sf_id__contains: query };
    if (query.includes("@")) return { contact__email: query };
    return { subject__icontains: query };
}

/**
 * Option sources consumed by `Combobox` and by the list filters. They are routes rather
 * than direct calls so components keep going through the data router.
 */
const DialerPicklistsRoute: RouteObject = {
    path: "picklists",
    children: [
        {
            path: "campagnes",
            loader: async () => DataService.client.url(`/dialer/campaigns/picklist/`).get().json()
        },
        {
            // Carrier objects for the simulator. The opportunity endpoint has no free-text
            // search, so the query is routed to the filter that can serve it: a Salesforce
            // id when it looks like one, the client's name otherwise.
            path: "opportunites",
            loader: async ({ request }) => {
                const query = (new URL(request.url).searchParams.get("q") ?? "").trim();
                const looksLikeSalesforceId = /^[a-zA-Z0-9]{15,18}$/.test(query);
                const filters = query ? (looksLikeSalesforceId ? { sf_id__contains: query } : { name__icontains: query }) : {};
                const response = (await DataService.client
                    .url(`/salesforce/opportunities/`)
                    .query({ ...filters, page_size: 20 })
                    .get()
                    .json()) as { results?: { id: number; name?: string | null; sf_id?: string | null }[] };
                return (response.results ?? []).map(opportunity => ({
                    // The simulator resolves either an internal id or a Salesforce id, and
                    // the latter is what a person recognises in a journal.
                    value: opportunity.sf_id || String(opportunity.id),
                    label: opportunity.name || opportunity.sf_id || `#${opportunity.id}`
                }));
            }
        },
        {
            // Same routing for cases, plus the contact's email: case subjects repeat
            // ("BI à signer"), so the label carries the Salesforce id to tell them apart.
            path: "cases",
            loader: async ({ request }) => {
                const query = (new URL(request.url).searchParams.get("q") ?? "").trim();
                const response = (await DataService.client
                    .url(`/salesforce/cases/`)
                    .query({ ...caseFilters(query), page_size: 20 })
                    .get()
                    .json()) as { results?: { id: number; subject?: string | null; sf_id?: string | null }[] };
                return (response.results ?? []).map(sfCase => ({
                    value: sfCase.sf_id || String(sfCase.id),
                    label: [sfCase.subject, sfCase.sf_id].filter(Boolean).join(" · ") || `#${sfCase.id}`
                }));
            }
        }
    ]
};

export default DialerPicklistsRoute;
